/**
 * Core Hook Handlers — Session Start/End + Tool Use
 *
 * Handles the original 4 Claude Code events:
 *   SessionStart → emit root agent span (kind: agent); push onto parentStack
 *   SessionEnd   → close ALL orphan container spans then root; delete state
 *   PreToolUse   → emit tool span with current parent from stack
 *   PostToolUse  → close matching open tool span with status: completed
 */

import { randomUUID } from 'crypto';
import {
  readState,
  writeState,
  deleteState,
  pushParent,
  peekParent,
  type HookState,
  type SpanPayload,
} from './state.js';
import { sendBatchTrace } from './api.js';

// ============================================================================
// Stdin parsing
// ============================================================================

export async function readStdin(): Promise<Record<string, unknown>> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) {
    chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk as string));
  }
  return JSON.parse(Buffer.concat(chunks).toString('utf-8')) as Record<string, unknown>;
}

// ============================================================================
// Non-blocking wrapper
// ============================================================================

export async function safeExecute(fn: () => Promise<void>): Promise<void> {
  try {
    await fn();
  } catch (err) {
    process.stderr.write(
      `[mutagent hooks] Warning: ${err instanceof Error ? err.message : String(err)}\n`
    );
  }
}

// ============================================================================
// Helpers
// ============================================================================

export function getString(input: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const val = input[key];
    if (typeof val === 'string' && val.length > 0) return val;
  }
  return '';
}

export function serializePayload(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  return typeof value === 'string' ? value : JSON.stringify(value);
}

// ============================================================================
// SessionStart — emit root agent span; push onto parentStack
// ============================================================================

export async function handleSessionStart(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const traceId = `cc-${sessionId}`;
  const rootSpanId = randomUUID();

  const state: HookState = {
    traceId,
    sessionId,
    startTime: now,
    openSpans: {},
    parentStack: [],
    parentStackKinds: [],
  };
  pushParent(state, rootSpanId, 'agent');
  writeState(sessionId, state);

  await sendBatchTrace([
    {
      traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime: now,
      status: 'running',
      spans: [
        {
          spanId: rootSpanId,
          name: 'Claude Code Session',
          kind: 'agent',
          startTime: now,
          status: 'running',
          parentSpanId: null,
        },
      ],
    },
  ]);
}

// ============================================================================
// SessionEnd — close root trace; clean up state
// ============================================================================

export async function handleSessionEnd(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId);
  const traceId = state?.traceId ?? `cc-${sessionId}`;
  const startTime = state?.startTime ?? now;

  const spansToClose: SpanPayload[] = [];

  if (state && state.parentStack.length > 0) {
    // Close orphan container spans (depth > 0 = not yet popped by their Stop/SubagentStop)
    // Walk from top down; spans at depth > 0 are orphans (subagent crash, missing stop, etc.)
    // The root span (index 0) is closed last with status=completed; orphans get status=error.
    const stackCopy = [...state.parentStack];
    const kindsCopy = [...state.parentStackKinds];

    // Pop orphans (index > 0) with status=error
    for (let i = stackCopy.length - 1; i > 0; i--) {
      const orphanId = stackCopy[i];
      const orphanKind = kindsCopy[i] ?? 'agent';
      if (orphanId) {
        spansToClose.push({
          spanId: orphanId,
          name: 'orphaned span',
          kind: orphanKind,
          startTime: now,
          endTime: now,
          status: 'error',
          output: 'orphaned at session end',
          parentSpanId: stackCopy[i - 1] ?? null,
        });
      }
    }

    // Close root (index 0) with status=completed
    const rootSpanId = stackCopy[0];
    if (rootSpanId) {
      spansToClose.push({
        spanId: rootSpanId,
        name: 'Claude Code Session',
        kind: 'agent',
        startTime: state.startTime,
        endTime: now,
        status: 'completed',
        parentSpanId: null,
      });
    }
  }

  await sendBatchTrace([
    {
      traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime,
      endTime: now,
      status: 'completed',
      spans: spansToClose,
    },
  ]);

  deleteState(sessionId);
}

// ============================================================================
// PreToolUse — emit tool span with current parent
// ============================================================================

export async function handlePreToolUse(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  const toolName = getString(input, 'tool_name', 'toolName') || 'unknown';
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId) ?? {
    traceId: `cc-${sessionId}`,
    sessionId,
    startTime: now,
    openSpans: {} as Record<string, { spanId: string; startTime: string; toolName: string }>,
    parentStack: [] as string[],
    parentStackKinds: [] as string[],
  };

  const parentSpanId = peekParent(state);
  const spanId = randomUUID();

  state.openSpans[spanId] = {
    spanId,
    startTime: now,
    toolName,
  };
  writeState(sessionId, state);

  const toolInput = serializePayload(input.tool_input ?? input.toolInput);

  await sendBatchTrace([
    {
      traceId: state.traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime: state.startTime,
      status: 'running',
      spans: [
        {
          spanId,
          name: toolName,
          kind: 'tool',
          startTime: now,
          status: 'running',
          ...(toolInput !== undefined ? { input: toolInput } : {}),
          ...(parentSpanId !== null ? { parentSpanId } : {}),
        },
      ],
    },
  ]);
}

// ============================================================================
// PostToolUse — close matching tool span with status: completed
// ============================================================================

export async function handlePostToolUse(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  const toolName = getString(input, 'tool_name', 'toolName') || 'unknown';
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId);
  const traceId = state?.traceId ?? `cc-${sessionId}`;
  const startTime = state?.startTime ?? now;

  // Find matching open span by toolName (LIFO — most recent match)
  let matchedSpanId: string | null = null;
  let matchedSpanStart: string = now;
  let matchedKey: string | null = null;

  if (state?.openSpans) {
    const entries = Object.entries(state.openSpans);
    for (let i = entries.length - 1; i >= 0; i--) {
      const entry = entries[i];
      if (entry && entry[1].toolName === toolName) {
        matchedSpanId = entry[1].spanId;
        matchedSpanStart = entry[1].startTime;
        matchedKey = entry[0];
        break;
      }
    }
  }

  const spanId = matchedSpanId ?? randomUUID();
  const spanStartTime = matchedSpanStart;

  if (state && matchedKey) {
    state.openSpans = Object.fromEntries(
      Object.entries(state.openSpans).filter(([k]) => k !== matchedKey)
    );
    writeState(sessionId, state);
  }

  const rawOutput =
    input.tool_response ??      // Claude Code actual key — must be first
    input.toolResponse ??
    input.tool_output ?? input.toolOutput ??
    input.tool_result ?? input.toolResult ??
    input.output ?? input.result;
  const toolOutput = serializePayload(rawOutput);

  await sendBatchTrace([
    {
      traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime,
      status: 'running',
      spans: [
        {
          spanId,
          name: toolName,
          kind: 'tool',
          startTime: spanStartTime,
          endTime: now,
          status: 'completed',
          ...(toolOutput !== undefined ? { output: toolOutput } : {}),
        },
      ],
    },
  ]);
}
